import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Loader2, ZoomIn, Move } from 'lucide-react';
import { useLang } from '../i18n';

const VIEWPORT = 256;   // square crop viewport (and output) size in px
const OUTPUT = 256;

/**
 * Lets the user zoom and reposition an image inside a square frame before it is
 * saved as their avatar. Exports a 256x256 JPEG blob via onConfirm(blob).
 */
export const AvatarCropDialog = ({ file, open, onCancel, onConfirm, busy }) => {
  const { lang } = useLang();
  const en = lang === 'en';
  const t = (de, eng) => (en ? eng : de);

  const [url, setUrl] = useState('');
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const imgElRef = useRef(null);
  const drag = useRef(null);

  // Load the picked file into an object URL + read natural dimensions.
  useEffect(() => {
    if (!file) { setUrl(''); return undefined; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    const im = new Image();
    im.onload = () => {
      setNat({ w: im.naturalWidth, h: im.naturalHeight });
      imgElRef.current = im;
    };
    im.src = u;
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const baseScale = nat.w && nat.h ? VIEWPORT / Math.min(nat.w, nat.h) : 1;
  const effScale = baseScale * zoom;
  const sw = nat.w * effScale;
  const sh = nat.h * effScale;

  const clamp = useCallback((p, SW, SH) => ({
    x: Math.min(0, Math.max(VIEWPORT - SW, p.x)),
    y: Math.min(0, Math.max(VIEWPORT - SH, p.y)),
  }), []);

  // Center the image whenever it loads or zoom changes (around the center).
  useEffect(() => {
    if (!sw || !sh) return;
    setPos((prev) => {
      // Keep current center stable on zoom; initial pos (0,0) centers below.
      const centered = { x: (VIEWPORT - sw) / 2, y: (VIEWPORT - sh) / 2 };
      const base = (prev.x === 0 && prev.y === 0) ? centered : prev;
      return clamp(base, sw, sh);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sw, sh]);

  const onPointerDown = (e) => {
    drag.current = { startX: e.clientX, startY: e.clientY, orig: { ...pos } };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const nx = drag.current.orig.x + (e.clientX - drag.current.startX);
    const ny = drag.current.orig.y + (e.clientY - drag.current.startY);
    setPos(clamp({ x: nx, y: ny }, sw, sh));
  };
  const onPointerUp = () => { drag.current = null; };

  const handleConfirm = async () => {
    const im = imgElRef.current;
    if (!im) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT;
    canvas.height = OUTPUT;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT, OUTPUT);
    const ratio = OUTPUT / VIEWPORT;
    ctx.drawImage(im, pos.x * ratio, pos.y * ratio, sw * ratio, sh * ratio);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (blob) onConfirm(blob);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="sm:max-w-[360px]" data-testid="avatar-crop-dialog">
        <DialogHeader>
          <DialogTitle>{t('Bild zuschneiden', 'Crop image')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <div
            className="relative overflow-hidden rounded-full border border-border bg-muted"
            style={{ width: VIEWPORT, height: VIEWPORT, maxWidth: '100%', touchAction: 'none', cursor: 'grab' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            data-testid="avatar-crop-area"
          >
            {url ? (
              <img
                src={url}
                alt="crop"
                draggable={false}
                style={{
                  position: 'absolute',
                  left: 0, top: 0,
                  width: sw, height: sh,
                  transform: `translate(${pos.x}px, ${pos.y}px)`,
                  userSelect: 'none', pointerEvents: 'none',
                }}
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            )}
          </div>

          <div className="flex w-full items-center gap-3">
            <ZoomIn className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Slider
              value={[zoom]}
              min={1}
              max={3}
              step={0.01}
              onValueChange={(v) => setZoom(v[0])}
              data-testid="avatar-crop-zoom"
              aria-label={t('Zoom', 'Zoom')}
            />
          </div>
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Move className="h-3.5 w-3.5" />
            {t('Ziehen zum Verschieben, Regler zum Zoomen', 'Drag to move, slider to zoom')}
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy} data-testid="avatar-crop-cancel">
            {t('Abbrechen', 'Cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={busy || !url} className="gap-1.5" data-testid="avatar-crop-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t('Speichern', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
